import { Button, EmptyState, Menu, SortableCell, SortableHead, SortableList } from "@omnabase/ui";
import { collectionSortLabel } from "@omnabase/shared";
import { useSession } from "../../shell/store";
import { useSetParam, useWs } from "../../lib/nav";
import { kb, type KbCollection, type KnowledgeOverview, type Scope } from "../../lib/knowledge";
import { useDroppedOrder } from "../../lib/order";

/** The collections screen (one management screen, the founder, 2026-10-12: "a table of collections where we add and delete collections and
 *  move them by a drag-and-drop handle, with an edit column opening a dialog"): the scope's collections in the team's order (the help
 *  centre's), each with its article count and its sort in words, a drag handle, Edit and the row menu's Delete…; New collection is the
 *  page's one button. The dialogs are `CollectionDialogs`' (`collections=new|edit-<id>|delete-<id>`). Its address:
 *  `/knowledge/collections?project=<id>`, reached by the pencil beside the views column's Collections heading. */
export function CollectionsManage({ scope, overview: o, reload }: { scope: Scope; overview: KnowledgeOverview; reload: () => void }) {
  const { slug } = useWs(); const { toast } = useSession(); const setParam = useSetParam();
  const order = useDroppedOrder(o.collections.map((c) => c.id));
  const rows = order.ids.map((id) => o.collections.find((c) => c.id === id)).filter((c): c is KbCollection => !!c);
  const ids = rows.map((c) => c.id);
  const save = (next: string[]) => kb.orderCollections(slug, scope.id, next).then(() => reload());
  const onDrop = (from: number, to: number) => void order.drop(ids, from, to, save).catch((e: Error) => toast(e.message, { kind: "error" }));
  const articlesIn = (c: KbCollection) => o.articles.filter((a) => a.collectionId === c.id && a.status !== "archived").length;
  return (
    <section className="ob-listpage" aria-label="Collections">
      <div className="ob-listpage__head">
        <h1 className="ob-listpage__title">Collections</h1>
        <span className="ob-listpage__count">{rows.length} {rows.length === 1 ? "collection" : "collections"}</span>
        <div className="ob-listpage__tools">
          <Button variant="primary" size="sm" icon="plus" onClick={() => setParam("collections", "new", true)}>New collection</Button>
        </div>
      </div>
      <div className="ob-listpage__scroll"><div className="ob-manage">
        <p className="ob-manage__intro">The help centre shows the collections in this order. Drag a row by its handle to move it.</p>
        {rows.length === 0 ? <EmptyState icon="folderOpen" title="No collections yet" text="Make the first one with New collection." /> : (
          <div className="ob-dtable-card">
            <SortableList ids={ids} onDrop={onDrop}>
            {(drag) => (
            <table className="ob-dtable ob-dtable--manage" aria-label="Collections">
              <thead><tr><SortableHead /><th>Name</th><th className="ob-dtable__center">Articles</th><th className="ob-dtable__col--below-540">Sort</th><th className="ob-dtable__actions"><span className="ob-sr-only">Actions</span></th></tr></thead>
              <tbody>
                {rows.map((c, i) => (
                  <tr key={c.id} {...drag.rowProps(i)}>
                    <SortableCell>{drag.handle(i, c.name)}</SortableCell>
                    <td className="ob-dtable__grow"><span className="ob-dtable__name" title={c.name}>{c.name}</span>{c.description && <span className="ob-dtable__desc">{c.description}</span>}<span className="ob-dtable__desc ob-dtable__desc--narrow">{collectionSortLabel(c.sort)}</span></td>
                    <td className="ob-dtable__center">{" "}{articlesIn(c)}{" "}</td>{/* the spaces keep the number a word of its own in the row's text */}
                    <td className="ob-dtable__col--below-540 ob-dtable__muted">{collectionSortLabel(c.sort)}</td>
                    <td className="ob-dtable__actions"><span className="ob-dtable__row-actions">
                      <Button size="sm" onClick={() => setParam("collections", `edit-${c.id}`, true)}>Edit</Button>
                      <Menu align="end" trigger={<Button variant="icon" size="sm" icon="more" aria-label={`More for ${c.name}`} />} sections={[{ items: [
                        { id: "delete", label: "Delete…", icon: "trash", onSelect: () => setParam("collections", `delete-${c.id}`, true) },
                      ] }]} />
                    </span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            )}
            </SortableList>
          </div>
        )}
      </div></div>
    </section>
  );
}
